import { GuestType } from "@/graphql/types";
import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import Modal from "../common/modal/modal";
import SelectItem from "../common/input/selectItem";
import GuestInput from "../common/input/guestInput";
import Button from "../common/button/button";

type Props = {
  guestTypes: GuestType[];
  stageId: number;
  onSubmit: (guestType: GuestType, isNew: boolean) => Promise<void>;
  onDelete: (guestType: GuestType) => Promise<void>;
  onClose: () => void;
};

const EditGuestType = (props: Props) => {
  const [selectedGuestType, setSelectedGuestType] = useState(
    undefined as GuestType | undefined,
  );
  const [inputError, setInputError] = useState(false);
  const [newValue, setNewValue] = useState(undefined as GuestType | undefined);
  const [showConfirm, setShowConfirm] = useState(false);

  const guestTypes = useMemo(() => {
    return [
      {
        id: -1,
        name: "*新規作成",
        stageId: props.stageId,
      } as GuestType,
      ...props.guestTypes,
    ];
  }, [props.guestTypes, props.stageId]);

  const onSelected = useCallback(
    (guestType: GuestType) => {
      if (guestType.id === -1) {
        setSelectedGuestType({
          id: -1,
          name: "",
          stageId: props.stageId,
        });
      } else {
        setSelectedGuestType(guestType);
      }
    },
    [props.stageId],
  );

  const onNameChanged = useCallback(
    (name: string, errorOccurs: boolean) => {
      setInputError(errorOccurs);
      if (!selectedGuestType) {
        return;
      }
      setNewValue({
        id: selectedGuestType?.id,
        name,
        stageId: selectedGuestType.stageId,
      });
    },
    [selectedGuestType],
  );

  const saveDisabled = useMemo(() => {
    return !newValue || inputError;
  }, [inputError, newValue]);

  const saveChange = useCallback(async () => {
    if (saveDisabled) {
      return;
    }
    if (newValue !== undefined) {
      const isNew = newValue.id === -1;
      await props.onSubmit(newValue, isNew);
    }
    props.onClose();
  }, [newValue, props, saveDisabled]);

  const deleteDisabled = useMemo(() => {
    return !selectedGuestType || selectedGuestType.id === -1;
  }, [selectedGuestType]);

  const deleteItem = useCallback(async () => {
    if (deleteDisabled) {
      return;
    }
    await props.onDelete(selectedGuestType!);
    props.onClose();
  }, [deleteDisabled, props, selectedGuestType]);

  const components = useMemo(() => {
    return (
      <>
        <Modal title="観客種別編集" onClose={props.onClose}>
          <EditGuestTypeStyle>
            <div className="edit-guest-wrapper">
              <div className="guide-message">
                観客種別を新規作成するか、既存の種別を選んで編集してください。
              </div>
              <div className="guest-type-select">
                <SelectItem
                  label="観客種別"
                  itemDict={guestTypes}
                  unselected={true}
                  nameKey="name"
                  width={300}
                  handleChange={(guestType) => onSelected(guestType)}
                />
              </div>
              {selectedGuestType ? (
                <>
                  {guestTypes.map((guestType) =>
                    selectedGuestType.id === guestType.id ? (
                      <div
                        key={"guest-type-input-" + guestType.id}
                        className="guest-type-input"
                      >
                        <GuestInput
                          id="guest-type-edit-name"
                          defaultValue={selectedGuestType.name}
                          placeholder="種別名を入力"
                          literalValidation={
                            /^[^!@#$%^&*()_+{}/\[\]:;<>,.?~=-]+$/
                          }
                          error={inputError}
                          errorMessage="使用できない名称です"
                          label="観客種別名"
                          onChange={(name, errorOccurs) => {
                            onNameChanged(name, errorOccurs);
                          }}
                        />
                      </div>
                    ) : (
                      <></>
                    ),
                  )}
                  <div className="buttons">
                    <div className="button">
                      <Button
                        label="保存"
                        buttonType="forward"
                        disabled={saveDisabled}
                        onClick={saveChange}
                        width={80}
                      />
                    </div>
                    <div className="button">
                      <Button
                        label="削除"
                        buttonType="delete"
                        disabled={deleteDisabled}
                        onClick={() => {
                          setShowConfirm(true);
                        }}
                        width={80}
                      />
                    </div>
                    <div className="button">
                      <Button
                        label="キャンセル"
                        buttonType="back"
                        onClick={props.onClose}
                        width={110}
                      />
                    </div>
                    <div className="cancel-button"></div>
                  </div>
                </>
              ) : (
                <></>
              )}
            </div>
          </EditGuestTypeStyle>
        </Modal>
        {showConfirm && selectedGuestType ? (
          <Modal
            title="観客種別削除"
            onClose={() => {
              setShowConfirm(false);
            }}
          >
            <EditGuestTypeStyle>
              <div>
                観客種別「{selectedGuestType.name}」を削除してよろしいですか？
              </div>
              <div className="buttons">
                <div className="button">
                  <Button
                    buttonType="delete"
                    label="削除"
                    onClick={deleteItem}
                    width={80}
                  />
                </div>
                <div className="button">
                  <Button
                    buttonType="back"
                    label="キャンセル"
                    onClick={() => {
                      setShowConfirm(false);
                    }}
                    width={110}
                  />
                </div>
              </div>
            </EditGuestTypeStyle>
          </Modal>
        ) : (
          <></>
        )}
      </>
    );
  }, [
    deleteDisabled,
    deleteItem,
    guestTypes,
    inputError,
    onNameChanged,
    onSelected,
    props.onClose,
    saveChange,
    saveDisabled,
    selectedGuestType,
    showConfirm,
  ]);

  return components;
};

const EditGuestTypeStyle = styled.div`
  .edit-guest-wrapper {
    .guide-message {
      font-size: 16px;
      margin-bottom: 10px;
    }
    .guest-type-select {
      margin: 0 auto 16px auto;
      text-align: center;
    }
    .guest-type-input {
      margin: 0 auto 16px auto;
      text-align: center;
    }
  }
  .buttons {
    display: flex;
    justify-content: center;
    gap: 0;
    text-align: center;
    margin: auto;
    .button {
      margin: 0 4px 0 4px;
    }
  }
`;

export default EditGuestType;
